import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartOptions } from 'chart.js';
import { CardComponent } from '@custom-components/general/card';
import {
  ChartComponent,
  XemexChartDataset,
} from '@custom-components/general/chart/chart.component';
import { TagComponent } from '@custom-components/general/tag/tag.component';
import {
  PerformanceRatios,
  performanceVerdict,
  PRODUCTION_STOPPED_BELOW,
  UNDERPERFORMING_BELOW,
} from './performance-verdict';

// One day of the rolling trend: the date it belongs to, and that day's 7-day ratio.
// `null` marks a day whose window could not be evaluated, so the line breaks instead of
// dropping to zero.
export interface PerformanceTrendPoint {
  date: string;
  ratio: number | null;
}

// The trend's vertical window starts from a fixed span, so the 0.70 threshold sits in the
// same visual place on every device — the point of drawing it. The window only ever grows:
// a device ratio below 0.60 would otherwise be drawn off-scale and clipped away, leaving a
// card that looks like it has no data at all while reporting "Underperforming".
const BASE_Y_MIN = 0.6;
const BASE_Y_MAX = 1.05;
const Y_PADDING = 0.05;
// Caps how far the window grows: a mis-configured device reporting far above 1.0 would otherwise
// rescale the real series into a hairline. Days above the ceiling are clamped to it rather than
// clipped, because a gap in the line already means "could not be evaluated". Their exact value
// stays readable in the stats above the chart.
const Y_MAX_CEILING = 1.5;
// Gridlines every 0.10. The step divides both reference values, so 0.70 and 1.00 always land
// on the ladder rather than sitting between two lines.
const TICK_STEP = 0.1;
// How many dates the x-axis names at most. Labelling all sixty overlaps into a smear; a handful,
// evenly spaced, is what tells a reader which window the line covers - and that the window
// actually moved when another day is picked.
const MAX_X_LABELS = 5;
const EPSILON = 1e-9;

// Chart.js is handed values, not classes, and there is no TypeScript build of the design
// tokens yet, so these mirror `_tokens.generated.scss` by hand. Keep them in step with that
// file: $token-chart-primary, $token-text-error, $token-text-tertiary, $token-text-muted en
// $token-text-inverse.
const RATIO_LINE = '#16A085';
const THRESHOLD = '#BA1A1A';
// De 1.0-lijn is lichter dan zijn eigen label, zodat de lijn de reeks niet overstemt.
const REFERENCE_LINE = '#AAABAD';
const REFERENCE_LABEL = '#8F9193';
// De tussenliggende gridlijnen zijn lichter dan beide referentielijnen, zodat het raster
// diepte geeft zonder met de 0.70-drempel te concurreren. Mirrors $token-border-subtle.
const GRIDLINE = '#DFE3E7';
// Ring om de eindpunt-dot, zodat hij loskomt van de lijn eronder.
const DOT_RING = '#FFFFFF';
const LAST_POINT_RADIUS = 5;
// Zwaarder dan de gedeelde default (2), omdat deze reeks over 60 dagen vrijwel vlak loopt.
const LINE_WIDTH = 3;

const TOOLTIP_LABEL_KEY = 'devices.detailTabs.insights.performance.tooltipLabel';

const DECIMALS = 2;
const EMPTY_VALUE = '–';

// Stepping by 0.1 in binary floating point drifts (0.7999999999999999), which would miss the
// exact `=== 0.7` and `=== 1` comparisons the reference colours depend on.
function round(value: number): number {
  return Math.round(value * 100) / 100;
}

@Component({
  selector: 'xemex-performance-ratio-insights',
  imports: [MatIconModule, TranslateModule, CardComponent, ChartComponent, TagComponent],
  templateUrl: './performance-ratio.component.html',
  styleUrl: './performance-ratio.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerformanceRatioComponent {
  readonly ratios = input.required<PerformanceRatios>();
  readonly trend = input.required<PerformanceTrendPoint[]>();
  readonly loading = input<boolean>(false);
  // The day the ratios describe. Shown in the last-day stat's label so it is never
  // ambiguous which day a reader is looking at.
  readonly anchorDate = input<Date | null>(null);

  private readonly translate = inject(TranslateService);

  readonly verdict = computed(() => performanceVerdict(this.ratios()));

  // A window that could not be evaluated reads as an en-dash, and a trend without a single
  // valid day draws an empty ladder. Three dashes over a blank grid is the least informative
  // thing this card can say, so the two halves are shown only when they carry a measurement,
  // and a device with neither gets an explanation instead.
  readonly hasRatio = computed(() => Object.values(this.ratios()).some(ratio => ratio !== null));

  readonly hasTrend = computed(() => this.trend().some(point => point.ratio !== null));

  readonly hasData = computed(() => this.hasRatio() || this.hasTrend());

  readonly sevenDayLabel = computed(() => this.format(this.ratios().sevenDay));

  // The headline number carries the same three tiers as the verdict chip beside it, so a
  // reader sees the judgement in the value itself and not only in the chip's wording.
  // Read off the seven-day ratio alone: this styles that number, not the card's verdict,
  // which also weighs the last day and the thirty-day window.
  readonly sevenDayTone = computed<'good' | 'warning' | 'critical' | 'unknown'>(() => {
    const ratio = this.ratios().sevenDay;
    if (ratio === null) return 'unknown';
    if (ratio < PRODUCTION_STOPPED_BELOW) return 'critical';
    return ratio < UNDERPERFORMING_BELOW ? 'warning' : 'good';
  });
  readonly lastDayLabel = computed(() => this.format(this.ratios().lastCompleteDay));
  readonly thirtyDayLabel = computed(() => this.format(this.ratios().thirtyDay));

  // "13 Apr" in the active language, or an en-dash when no day could be resolved.
  readonly anchorDayLabel = computed(() => {
    const date = this.anchorDate();
    if (date === null) return EMPTY_VALUE;
    return new Intl.DateTimeFormat(this.translate.currentLang, {
      day: 'numeric',
      month: 'short',
    }).format(date);
  });

  readonly trendLabels = computed(() =>
    this.trend().map(point => {
      const parsed = new Date(point.date);
      return Number.isNaN(parsed.getTime())
        ? point.date
        : new Intl.DateTimeFormat(this.translate.currentLang, {
            day: 'numeric',
            month: 'short',
          }).format(parsed);
    }),
  );

  // Spaced from the last day backwards, so the anchor day is always named however many days the
  // trend happens to carry. The first day is added when it is not already close to a labelled
  // one, because the left edge is half of "which window is this".
  private readonly labelledTrendIndices = computed(() => {
    const count = this.trend().length;
    const indices = new Set<number>();
    if (count === 0) return indices;
    const step = Math.max(1, Math.ceil((count - 1) / (MAX_X_LABELS - 1)));
    for (let index = count - 1; index >= 0; index -= step) indices.add(index);
    if (Math.min(...indices) >= step / 2) indices.add(0);
    return indices;
  });

  readonly trendDatasets = computed<XemexChartDataset[]>(() => {
    const ceiling = this.trendWindow().max;
    return [
      {
        // Names the series in the tooltip; the caption under the chart carries the longer
        // wording, so repeating it inside a hover box only crowds the number it is there for.
        label: this.translate.instant(TOOLTIP_LABEL_KEY),
        // A day above the window's ceiling is pinned to it rather than drawn off-scale: an
        // off-scale segment is clipped away entirely, and a hole in the line is how this chart
        // says "could not be evaluated". A run along the top gridline stays visible instead.
        data: this.trend().map(point =>
          point.ratio === null ? null : Math.min(point.ratio, ceiling),
        ),
        type: 'line',
        color: RATIO_LINE,
        // The series is a single flat-ish line over 60 days; the area underneath is what makes
        // it read as a body of data rather than a hairline against the gridlines.
        fill: true,
      },
    ];
  });

  // Chart.js does not evaluate `min`/`max` per render, so the window is resolved here and
  // the options object is rebuilt whenever the trend changes.
  private readonly trendWindow = computed(() => {
    const ratios = this.trend()
      .map(point => point.ratio)
      .filter((ratio): ratio is number => ratio !== null);
    const lowest = Math.max(0, Math.min(BASE_Y_MIN, ...ratios.map(ratio => ratio - Y_PADDING)));
    return {
      // Snapped down to a whole step so the lowest gridline is the floor of the plot rather
      // than a stray line just above it. The epsilon keeps a value that is already on the
      // ladder there: 0.6 / 0.1 is 5.999… in binary, which would floor a step too far.
      min: round(Math.floor(lowest / TICK_STEP + EPSILON) * TICK_STEP),
      // Capped, so the number of gridlines stays bounded whatever the data does.
      max: Math.min(Y_MAX_CEILING, Math.max(BASE_Y_MAX, ...ratios.map(ratio => ratio + Y_PADDING))),
    };
  });

  // Every step between the window's bounds, so the series reads against a full ladder instead
  // of floating between two lone reference lines.
  private readonly trendTicks = computed(() => {
    const { min, max } = this.trendWindow();
    const ticks: { value: number }[] = [];
    for (let value = min; value <= max + EPSILON; value += TICK_STEP) {
      ticks.push({ value: round(value) });
    }
    return ticks;
  });

  readonly chartOptions = computed<ChartOptions>(() => ({
    maintainAspectRatio: false,
    elements: {
      // Het design tekent rechte segmenten tussen dagpunten. De gedeelde chart smooth't
      // standaard (`tension: 0.4`); caller-opties winnen van die default.
      line: { tension: 0, borderWidth: LINE_WIDTH },
      // Alleen het laatste punt krijgt een dot, de overige 59 blijven onzichtbaar.
      point: {
        radius: (context: { dataIndex: number; dataset: { data: unknown[] } }) =>
          context.dataIndex === context.dataset.data.length - 1 ? LAST_POINT_RADIUS : 0,
        backgroundColor: RATIO_LINE,
        borderColor: DOT_RING,
        borderWidth: 2,
      },
    },
    // A single series, and the two reference lines are axis gridlines rather than datasets,
    // so a legend would have nothing left to explain.
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          // Read from the trend, not from the plotted value: a day above the ceiling is pinned
          // to it for drawing, and a tooltip reporting that pinned number would misstate the
          // device's actual ratio - the one thing a reader hovers a point to find out.
          label: context =>
            `${this.translate.instant(TOOLTIP_LABEL_KEY)}: ` +
            this.format(this.trend()[context.dataIndex]?.ratio ?? null),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          callback: (_value, index) =>
            this.labelledTrendIndices().has(index) ? this.trendLabels()[index] : '',
        },
      },
      y: {
        min: this.trendWindow().min,
        max: this.trendWindow().max,
        // Chart.js' own tick generator would pick its own step for every window; this pins the
        // ladder to whole 0.10 steps so 1.0 ("matched expectation") and 0.70 (the
        // underperformance threshold) are always on it. Drawing those two as gridlines rather
        // than extra datasets keeps them out of the legend and out of the tooltip for free.
        afterBuildTicks: axis => {
          axis.ticks = this.trendTicks();
        },
        grid: { color: context => this.gridColour(context.tick?.value) },
        ticks: {
          color: context => this.labelColour(context.tick?.value),
          // The full ladder is drawn, but only three rungs are named: the threshold, the 1.0
          // reference, and the floor of the window. Numbering all sixteen of an extreme
          // window's gridlines turns the axis into a wall of decimals and buries the two
          // values that actually carry meaning.
          callback: value =>
            this.isLabelledTick(Number(value)) ? Number(value).toFixed(DECIMALS) : '',
        },
      },
    },
  }));

  private isLabelledTick(tickValue: number): boolean {
    return (
      tickValue === UNDERPERFORMING_BELOW || tickValue === 1 || tickValue === this.trendWindow().min
    );
  }

  // Three tiers: the threshold, the 1.0 reference, and the rest of the ladder behind them.
  private gridColour(tickValue: number | undefined): string {
    if (tickValue === UNDERPERFORMING_BELOW) return THRESHOLD;
    return tickValue === 1 ? REFERENCE_LINE : GRIDLINE;
  }

  private labelColour(tickValue: number | undefined): string {
    return tickValue === UNDERPERFORMING_BELOW ? THRESHOLD : REFERENCE_LABEL;
  }

  private format(ratio: number | null): string {
    return ratio === null ? EMPTY_VALUE : ratio.toFixed(DECIMALS);
  }
}
